import { defineStore, storeToRefs } from 'pinia'
import { ref, computed, toRefs } from 'vue'
// @ts-expect-error: vuex
import { useStore } from 'vuex'

import isNil from 'lodash.isnil'
import isString from 'lodash.isstring'
import merge from 'lodash.merge'
import omitBy from 'lodash.omitby'

import { AuditCommentSchema } from '@/schemas/comment'

import { useErrorModal } from '@/composables/useErrorModal'
import childDocumentService from '@/services/http/api/childDocument'
import resourceService from '@/services/http/api/resource'

import removeHyphen from '@/helpers/removeHyphen'
import { sleep } from '@/helpers/sleep'
import { useBeginningOfOurStoryStore } from '@/stores/childDocument/beginningOfOurStory'
import { useCoordinatorQuestionnaireStore } from '@/stores/childDocument/coordinatorQuestionnaire'
import { useDreamDrawingStore } from '@/stores/childDocument/dreamDrawing'
import { useGuardianConsentStore } from '@/stores/childDocument/guardianConsent'
import { useSummaryOfOurStoryStore } from '@/stores/childDocument/summaryOfOurStory'
import { useChildEditionFlowActionsStore } from '@/stores/childEditionFlowActions'

import type { TODO, DocumentName } from '@/types'
import { objectFilterNullish } from '@/helpers/objectFilterNullish'
import {
  shouldRetryGetActionForAssignedEndpoint,
  shouldRetryPostActionForAssignedEndpoint
} from '@/helpers/retryActionForOtherEndpoint'

const prepareData = (payload: TODO) => {
  if (payload) {
    const copiedPayload = { ...payload }
    let preparedData = copiedPayload.data
    preparedData = omitBy(preparedData, isNil)
    preparedData = omitBy(preparedData, (value) => value.length === 0)
    preparedData = objectFilterNullish(preparedData)
    if (copiedPayload?.documentType === 'guardian_consent') {
      preparedData = {
        ...preparedData?.address,
        ...preparedData?.childApplication,
        ...preparedData?.extraField,
        ...preparedData?.parentInfo
      }
      preparedData.postalCode = removeHyphen(preparedData?.postalCode)
    }
    if (copiedPayload?.documentType === 'dream_drawing') {
      const formData = new FormData()
      formData.append('likeDescription', preparedData?.likeDescription)
      if (preparedData.drawing) formData.append('drawing', preparedData?.drawing)
      preparedData = formData
    }
    copiedPayload.data = preparedData
    return copiedPayload
  }
  return {}
}

export const useChildDocumentStore = defineStore('childDocument', () => {
  const { setErrorModalOnError } = useErrorModal()
  const beginningOfOurStoryStore = useBeginningOfOurStoryStore()
  const coordinatorQuestionnaireStore = useCoordinatorQuestionnaireStore()
  const dreamDrawingStore = useDreamDrawingStore()
  const guardianConsentStore = useGuardianConsentStore()
  const summaryOfOurStoryStore = useSummaryOfOurStoryStore()
  const childEditionFlowActionsStore = useChildEditionFlowActionsStore()

  const vuexStore = useStore()

  type DefaultState = {
    document: TODO
    sendLoader: boolean
    acceptLoader: boolean
    loader: boolean
    commentLoader: boolean
    commentSuccess: TODO
    resource: TODO
    documentError: TODO
    editMode: boolean
    auditLoader: boolean
  }
  const defaultState: DefaultState = {
    document: null,
    sendLoader: false,
    acceptLoader: false,
    loader: false,
    commentLoader: false,
    commentSuccess: null,
    resource: null,
    documentError: null,
    editMode: false,
    auditLoader: false
  }

  const state = ref({ ...defaultState })

  const { coordinatorQuestionnaire } = storeToRefs(coordinatorQuestionnaireStore)
  const { fetchDrawingFiles, dreamDrawing } = storeToRefs(dreamDrawingStore)
  const { guardianConsent } = storeToRefs(guardianConsentStore)
  const { beginningOfOurStory } = storeToRefs(beginningOfOurStoryStore)
  const { summaryOfOurStory } = storeToRefs(summaryOfOurStoryStore)

  const getComments = computed(() => {
    return state.value.document?.audit_comments?.filter((comment: unknown) => {
      const parsedComment = AuditCommentSchema.safeParse(comment)
      return parsedComment.success
    })
  })

  const clear = () => {
    Object.assign(state.value, defaultState)
  }
  const setDocument = (payload: TODO) => {
    state.value.document = {
      ...state.value.document,
      ...payload
    }
  }
  const setResouce = (payload: TODO) => {
    state.value.resource = merge(state.value.resource, payload)
  }

  const documentSetterMap = {
    coordinator_questionnaire: coordinatorQuestionnaireStore.setCoordinatorQuestionnaire,
    dream_drawing: dreamDrawingStore.setDreamDrawing,
    guardian_consent: guardianConsentStore.setGuardianConsent,
    beginning_of_our_story: beginningOfOurStoryStore.setBeginningOfOurStory,
    summary_of_our_story: summaryOfOurStoryStore.setSummaryOfOurStory
  }

  const documentGetterMap = {
    coordinator_questionnaire: coordinatorQuestionnaire,
    dream_drawing: dreamDrawing,
    guardian_consent: guardianConsent,
    beginning_of_our_story: beginningOfOurStory,
    summary_of_our_story: summaryOfOurStory
  }

  const documentClearMap = {
    coordinator_questionnaire: coordinatorQuestionnaireStore.clearCoordinatorQuestionnaire,
    dream_drawing: dreamDrawingStore.clearDreamDrawing,
    guardian_consent: guardianConsentStore.clearGuardianConsent,
    beginning_of_our_story: beginningOfOurStoryStore.clearBeginningOfOurStory,
    summary_of_our_story: summaryOfOurStoryStore.clearSummaryOfOurStory
  }

  // previously named getDocument
  const fetchDocument = async (payload: TODO) => {
    try {
      const { data } = await childDocumentService.getByType(payload, payload.getAssignedFirst)
      state.value.document = data?.data
      const documentType: DocumentName = payload?.documentType
      documentSetterMap[documentType](data?.data?.content)
      state.value.loader = false
      return data?.data
    } catch (error) {
      if (shouldRetryGetActionForAssignedEndpoint(error, payload)) {
        await fetchDocument({ ...payload, getAssignedFirst: true })
      } else throw error
    }
  }
  // previously named getResource
  const fetchResource = async (payload: TODO) => {
    try {
      const isPublic = payload.isPublic === true
      const { data } = await resourceService.document(
        payload.documentType,
        isPublic,
        payload?.editionName
      )
      setResouce(data?.data)
    } catch (error) {
      return Promise.reject(error)
    }
  }

  const init = async (payload: TODO) => {
    clear()
    beginningOfOurStoryStore.clearBeginningOfOurStory()
    coordinatorQuestionnaireStore.clearCoordinatorQuestionnaire()
    dreamDrawingStore.clearDreamDrawing()
    guardianConsentStore.clearGuardianConsent()
    summaryOfOurStoryStore.clearSummaryOfOurStory()

    state.value.loader = true
    let editionName
    try {
      editionName = await vuexStore.dispatch(
        'childEdition/getChildsEditionName',
        { ...payload },
        { root: true }
      )
    } catch (error) {
      console.error(error)
      return Promise.reject(error)
    }
    if (!isString(editionName) || editionName.length === 0) return null

    try {
      await fetchResource({ ...payload, editionName })
    } finally {
      try {
        await fetchDocument(payload)
      } catch {
        state.value.loader = false
      }
    }
  }

  const responseFunction = (data: TODO) => {
    const { school_hydra_users: schoolHydraUsers } =
      vuexStore.getters['childEdition/getChild'] ?? {}

    const childEdition = data?.data?.child_edition

    if (childEdition && typeof childEdition === 'object') {
      vuexStore.commit(
        'childEdition/setChild',
        {
          child_edition: childEdition,
          school_hydra_users: schoolHydraUsers
        },
        { root: true }
      )
    }
    vuexStore.commit('childEdition/setChildEditionRequestSkipped', true, { root: true })
    return data
  }

  const setDocumentFunction = ({ data }: TODO, payloadData: TODO) => {
    const prop = payloadData?.documentType
    const {
      data: { [prop]: document = null }
    } = data
    setDocument(document)
    const isComment = payloadData?.data?.content
    if (prop === 'dream_drawing' && !isComment && payloadData?.data?.get('drawing')) {
      fetchDrawingFiles.value = true
    }
  }

  const send = async (payload: TODO) => {
    state.value.sendLoader = true
    try {
      const payloadData = prepareData(payload)
      const { data } = await childDocumentService.post(payloadData, payloadData?.postAssignedFirst)
      responseFunction(data)
    } catch (error) {
      if (shouldRetryPostActionForAssignedEndpoint(error, payload)) {
        await send({ ...payload, postAssignedFirst: true })
      } else {
        setErrorModalOnError(error)
      }
    } finally {
      state.value.sendLoader = false
    }
  }

  const update = async (payload: TODO) => {
    state.value.sendLoader = true
    const payloadData = prepareData(payload)
    const method = payload?.method ?? 'post'

    try {
      const responseData = await childDocumentService.update(payloadData, method)
      setDocumentFunction(responseData, payloadData)
    } catch (error) {
      setErrorModalOnError(error)
    } finally {
      state.value.sendLoader = false
    }
  }

  const accept = async (payload: TODO) => {
    state.value.acceptLoader = true
    try {
      const response = await childDocumentService.accept(payload)
      setDocumentFunction(response, payload)
      responseFunction(response.data)
      childEditionFlowActionsStore.updateChildEditionFlowActionsFromBackendResponse(response.data)
    } catch (error) {
      setErrorModalOnError(error)
    } finally {
      state.value.acceptLoader = false
    }
  }

  const sendBack = async (payload: TODO) => {
    state.value.auditLoader = true
    try {
      const response = await childDocumentService.sendBack(payload)
      setDocumentFunction(response, payload)
      responseFunction(response.data)
    } catch (error) {
      setErrorModalOnError(error)
    } finally {
      state.value.auditLoader = false
    }
  }

  const sendToAccept = async (payload: TODO) => {
    state.value.auditLoader = true
    try {
      const response = await childDocumentService.sendToAccept(payload, payload?.postAssignedFirst)
      setDocumentFunction(response, payload)
      responseFunction(response.data)
    } catch (error) {
      if (shouldRetryPostActionForAssignedEndpoint(error, payload)) {
        await sendToAccept({ ...payload, postAssignedFirst: true })
      } else {
        setErrorModalOnError(error)
      }
    } finally {
      state.value.auditLoader = false
    }
  }

  const privilegedUpdate = async (payload: TODO) => {
    state.value.sendLoader = true
    const payloadData = prepareData(payload)
    const method = payload?.method ?? 'post'

    try {
      const responseData = await childDocumentService.privilegedUpdate(payloadData, method)
      setDocumentFunction(responseData, payloadData)
    } catch (error) {
      setErrorModalOnError(error)
    } finally {
      state.value.sendLoader = false
    }
  }

  const correction = async (payload: TODO) => {
    state.value.sendLoader = true
    const preparedData = prepareData(payload)
    const method = payload?.method ?? 'post'

    try {
      const response = await childDocumentService.correction(
        preparedData,
        preparedData?.postAssignedFirst,
        method
      )
      setDocumentFunction(response, preparedData)
    } catch (error) {
      if (shouldRetryPostActionForAssignedEndpoint(error, payload)) {
        await correction({ ...payload, postAssignedFirst: true })
      } else {
        setErrorModalOnError(error)
      }
    } finally {
      state.value.sendLoader = false
    }
  }

  const addComment = async (payload: TODO, successTimeout = 3500) => {
    state.value.commentLoader = true
    try {
      const response = await childDocumentService.addComment(payload, payload?.postAssignedFirst)
      setDocumentFunction(response, payload)
      state.value.commentSuccess = true
    } catch (error) {
      state.value.commentSuccess = false
      if (shouldRetryPostActionForAssignedEndpoint(error, payload)) {
        await addComment({ ...payload, postAssignedFirst: true })
      } else {
        setErrorModalOnError(error)
      }
    } finally {
      state.value.commentLoader = false
    }

    // Show the success (we use success icon in comment section)
    // and reset the flag after a timeout
    await sleep(successTimeout)
    state.value.commentSuccess = null
  }

  const setSendLoader = (loaderState: boolean): void => {
    state.value.sendLoader = loaderState
  }

  return {
    // state
    ...toRefs(state.value),
    documentSetterMap,
    documentGetterMap,
    documentClearMap,
    // getters
    getComments,
    // actions
    clear,
    fetchResource,
    init,
    send,
    update,
    accept,
    addComment,
    sendBack,
    sendToAccept,
    privilegedUpdate,
    correction,
    setSendLoader
  }
})
